import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { format, parseISO } from 'date-fns';

type SubmissionRow = {
  id: number;
  template_id: string;
  template_name: string | null;
  recipient_email: string;
  recipient_name: string | null;
  completed_at: string;
};

type ResponseItem = {
  questionId: string;
  questionText: string;
  questionType?: string;
  responseText: string;
};

const EmailWebSubmissionsPanel: React.FC = () => {
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<{
    template_name: string | null;
    completed_at: string;
    recipient_name: string | null;
    recipient_email: string;
    link_sent_at: string | null;
    responses: ResponseItem[] | null;
  } | null>(null);

  const fetchList = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/email-checks/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setRows(res.data.data || []);
      } else {
        setError('Could not load submissions');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/email-checks/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success && res.data.data) {
        const d = res.data.data;
        setDetail({
          template_name: d.template_name,
          completed_at: d.completed_at,
          recipient_name: d.recipient_name,
          recipient_email: d.recipient_email,
          link_sent_at: d.link_sent_at,
          responses: Array.isArray(d.responses) ? d.responses : null,
        });
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Email (web) submissions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        When someone completes a check via the public link (sent by email), the answers are stored here. They are not
        the same as app safety checks, which appear under <strong>Responses</strong>.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button
        startIcon={<RefreshIcon />}
        onClick={fetchList}
        disabled={loading}
        size="small"
        sx={{ mb: 2 }}
      >
        Refresh
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No web email submissions yet for this business.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Template</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    {r.completed_at
                      ? format(parseISO(r.completed_at), "dd MMM yyyy HH:mm")
                      : '—'}
                  </TableCell>
                  <TableCell>{r.template_name || r.template_id}</TableCell>
                  <TableCell>{r.recipient_name || '—'}</TableCell>
                  <TableCell>{r.recipient_email}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => openDetail(r.id)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Web submission</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : detail ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Template:</strong> {detail.template_name || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Recipient:</strong> {detail.recipient_name || '—'} &lt;{detail.recipient_email}&gt;
              </Typography>
              {detail.link_sent_at && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Link sent:</strong>{' '}
                  {format(parseISO(detail.link_sent_at), "dd MMM yyyy HH:mm")}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Completed:</strong>{' '}
                {detail.completed_at
                  ? format(parseISO(detail.completed_at), "dd MMM yyyy HH:mm")
                  : '—'}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Answers
              </Typography>
              {detail.responses && detail.responses.length > 0 ? (
                <Box component="ol" sx={{ m: 0, pl: 2 }}>
                  {detail.responses.map((ans, i) => (
                    <li key={i} style={{ marginBottom: 12 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ans.questionText}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {ans.responseText || '—'}
                      </Typography>
                    </li>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">No response data</Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailWebSubmissionsPanel;
